/**
 * MonthView (Plan 03-06).
 *
 * 6×7 CSS grid calendar with Monday-anchored week start (D-14 / FND-08).
 * Weekday header row: man / tir / ons / tor / fre / lør / søn (D-15).
 * #157 (steg 7a): restylet som ukestripen — et glasskort, én `role="row"` per
 * uke (axe-revisjonen av /kalender feilet på celler uten rad), og dagens dato
 * som fylt sirkel (`bg-solid`) med `aria-current="date"`.
 * Prev/next-month cells render in the secondary text colour (text-ink-2) and
 * normal weight. #151: uten opacity-60 — dempet slik ga 2.6:1,
 * under 4.5:1; vekt og nivå skiller dem fortsatt fra månedens dager.
 *
 * Each cell shows up to 2 compact EventCards; if more, appends "+N" overflow chip.
 *
 * D-06: no drag-and-drop. Tap on a cell or event opens via onDayClick / event onClick.
 */
import { useTranslation } from 'react-i18next';
import { addDays, isSameDay, isSameMonth } from 'date-fns';
import { startOfWeek as osloStartOfWeek } from '../../i18n/formatters';
import { EventCard } from './EventCard';
import { EmptyState } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import type { EventInstance } from './useCalendarRange';

/** Norwegian abbreviated weekday labels (Monday-start). */
const WEEKDAY_LABELS = ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'] as const;

const MAX_EVENTS_PER_CELL = 2;

/** Format a date to a YYYY-MM-DD key for bucketing. */
function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface MonthViewProps {
  anchor: Date;
  events: EventInstance[];
  onEventClick?: (event: EventInstance) => void;
  onDayClick?: (date: Date) => void;
}

export function MonthView({ anchor, events, onEventClick, onDayClick }: MonthViewProps) {
  const { t } = useTranslation();
  const today = new Date();

  // Build 42-cell grid starting from the Monday of the week containing month's 1st
  const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = osloStartOfWeek(monthStart);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(addDays(gridStart, i));
  }

  // Bucket events by date key (local date from starts_at)
  const byDay = new Map<string, EventInstance[]>();
  for (const ev of events) {
    // Use starts_at local date — all-day events float to top handled by ordering
    const key = dateKey(new Date(ev.starts_at));
    const arr = byDay.get(key) ?? [];
    arr.push(ev);
    byDay.set(key, arr);
  }

  const monthLabel = anchor.toLocaleDateString('nb-NO', {
    month: 'long',
    year: 'numeric',
  });

  const weeks: Date[][] = [];
  for (let w = 0; w < cells.length; w += 7) weeks.push(cells.slice(w, w + 7));

  return (
    <div className="flex flex-col gap-3">
      <div
        role="grid"
        aria-label={t('calendar.monthView.aria', { month: monthLabel })}
        data-testid="month-view"
        className={`${GLASS_SURFACE} rounded-lg p-2 shadow-md sm:p-3`}
      >
        {/* Weekday header row */}
        <div className="grid grid-cols-7" role="row">
          {WEEKDAY_LABELS.map((wk) => (
            <div
              key={wk}
              role="columnheader"
              className="p-1.5 text-center text-caption font-semibold text-ink-2"
              data-testid={`month-weekday-${wk}`}
            >
              {wk}
            </div>
          ))}
        </div>

        {/* 6×7 cell grid — én role="row" per uke (axe: aria-required-children/-parent) */}
        {weeks.map((week) => (
          <div key={dateKey(week[0]!)} role="row" className="grid grid-cols-7 gap-0.5">
            {week.map((d) => {
              const isToday = isSameDay(d, today);
              const inMonth = isSameMonth(d, anchor);
              const key = dateKey(d);
              const dayEvents = byDay.get(key) ?? [];
              const overflowCount = dayEvents.length - MAX_EVENTS_PER_CELL;

              return (
                <div
                  key={key}
                  role="gridcell"
                  aria-current={isToday ? 'date' : undefined}
                  data-testid={isToday ? 'month-cell-today' : `month-cell-${key}`}
                  onClick={() => onDayClick?.(d)}
                  className="flex min-h-16 min-w-0 cursor-pointer flex-col gap-0.5 rounded-md p-0.5 hover:bg-inset sm:min-h-24 sm:p-1.5"
                >
                  {/* Day-of-month digit — i dag er en fylt sirkel, som i ukestripen */}
                  <span
                    className={[
                      'flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-full text-control tnum sm:self-start',
                      isToday
                        ? 'bg-solid text-on-solid font-extrabold'
                        : inMonth
                          ? 'text-ink font-bold'
                          : 'text-ink-2',
                    ].join(' ')}
                  >
                    {d.getDate()}
                  </span>

                  {/* Compact event pills */}
                  {dayEvents.slice(0, MAX_EVENTS_PER_CELL).map((ev) => (
                    <EventCard
                      key={ev.id}
                      variant="compact"
                      id={ev.id}
                      title={ev.title}
                      starts_at={ev.starts_at}
                      ends_at={ev.ends_at}
                      all_day={ev.all_day}
                      color_token={ev.color_token}
                      attendee_ids={ev.attendee_ids}
                      onClick={() => onEventClick?.(ev)}
                    />
                  ))}

                  {/* Overflow count */}
                  {overflowCount > 0 && (
                    <span className="text-badge font-bold text-ink-2 leading-none">
                      +{overflowCount}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Empty month state — Plan 06-02 Task 2: <EmptyState variant="inline">
          using calendar.empty.month.{heading,body} per UI-SPEC §Copywriting.
          Outer wrapper preserves the existing month-empty data-testid. */}
      {events.length === 0 && (
        <div data-testid="month-empty">
          <EmptyState
            variant="inline"
            heading={t('calendar.empty.month.heading')}
            body={t('calendar.empty.month.body')}
          />
        </div>
      )}
    </div>
  );
}
